import { analytics } from '@/lib/analytics'
import { api } from '@/lib/api/api'
import { handleEmailVerificationRequired } from '@/lib/auth/emailVerification'
import { useEffect, useState } from 'react'
import { ApiOnboardingData, ApiOnboardingUpdateRequest } from 'sprites-types/api/onboarding'
import { ApiUserQuota } from 'sprites-types/api/user-quota'
import { ApiUser, ApiUserWorkspaceResponse, ApiWorkspace } from 'sprites-types/api/users'
import { signOut as supertokensSignOut } from 'supertokens-web-js/recipe/emailpassword'
import Session from 'supertokens-web-js/recipe/session'

export const userApi = api.injectEndpoints({
  endpoints: (builder) => ({
    signOut: builder.mutation<{ status: number; error: string }, void>({
      queryFn: async () => {
        analytics.userLifecycle.logout()
        await supertokensSignOut()
        analytics.reset()

        // Clear all localStorage to prevent data leakage between users
        if (typeof window !== 'undefined') {
          try {
            localStorage.clear()
          } catch (error) {
            console.error('Failed to clear localStorage on signout:', error)
          }
        }

        window.location.href = '/'
        return { data: { status: 200, error: 'Signed out' } }
      },
      invalidatesTags: ['User', 'UserQuota'],
    }),
    getUser: builder.query<{ user: ApiUser; workspace: ApiWorkspace }, void>({
      queryFn: async (_, _queryApi, _extraOptions, fetchWithBQ) => {
        // Let the backend handle session checking - it will return 400 for no session, 401 for invalid session
        const userResponse = await fetchWithBQ('v1/users/me')

        if (userResponse.error) {
          // Check for 403 - likely email verification required
          const error = userResponse.error as { status?: number; data?: { message?: string } }
          if (error.status === 403) {
            void handleEmailVerificationRequired(undefined)
          }
          return { error: userResponse.error }
        }

        const workspacesResponse = await fetchWithBQ('v1/users/me/workspace')

        if (workspacesResponse.error) {
          return { error: workspacesResponse.error }
        }
        if (!userResponse.data || !workspacesResponse.data) {
          return {
            error: {
              status: 401,
              data: { code: '401', message: 'Failed to fetch user or workspace' },
            },
          }
        }

        const user = userResponse.data as ApiUser
        const workspace = (workspacesResponse.data as ApiUserWorkspaceResponse).workspace

        analytics.identify(user.id, user.isAnonymous || false, {
          workspace_id: workspace.id,
          email: user.email,
          name: user.name,
        })

        return { data: { user, workspace } }
      },
      providesTags: ['User'],
    }),
    getUserQuota: builder.query<ApiUserQuota, void>({
      query: () => 'v1/users/me/quota',
      providesTags: ['UserQuota'],
    }),
    getOnboardingData: builder.query<ApiOnboardingData, void>({
      query: () => 'v1/users/me/onboarding',
      providesTags: ['OnboardingData'],
    }),
    updateOnboardingData: builder.mutation<
      { success: boolean; message: string; data: ApiOnboardingData },
      ApiOnboardingUpdateRequest
    >({
      query: (data) => ({
        url: 'v1/users/me/onboarding',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['OnboardingData'],
    }),
    getUserAdminStatus: builder.query<{ isAdmin: boolean }, void>({
      query: () => 'v1/users/me/admin-status',
      providesTags: ['UserAdminStatus'],
    }),
    getUserPrompt: builder.query<{ userPrompt: string | null }, { businessProfileId?: string }>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams()
        if (params.businessProfileId) {
          searchParams.set('businessProfileId', params.businessProfileId)
        }
        return `v1/users/me/prompt${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
      },
      providesTags: ['UserPrompt'],
    }),
    updateUserPrompt: builder.mutation<
      { success: boolean; message: string; userPrompt: string },
      { userPrompt: string; businessProfileId?: string }
    >({
      query: ({ userPrompt, businessProfileId }) => {
        const searchParams = new URLSearchParams()
        if (businessProfileId) {
          searchParams.set('businessProfileId', businessProfileId)
        }
        return {
          url: `v1/users/me/prompt${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
          method: 'PATCH',
          body: { userPrompt },
        }
      },
      invalidatesTags: ['UserPrompt'],
    }),
    getConnectedIntegrations: builder.query<
      { success: boolean; providerIds: string[] },
      { businessProfileId?: string }
    >({
      query: (params = {}) => {
        const searchParams = new URLSearchParams()
        if (params.businessProfileId) {
          searchParams.set('businessProfileId', params.businessProfileId)
        }
        return `v2/integrations/credentials${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
      },
      providesTags: ['IntegrationCredentials'],
    }),
    requestPasswordChange: builder.mutation<{ success: boolean; message: string }, void>({
      query: () => ({
        url: 'v1/users/me/change-password',
        method: 'POST',
      }),
    }),
    revokeAllSessions: builder.mutation<
      { success: boolean; message: string; revokedCount: number },
      void
    >({
      query: () => ({
        url: 'v1/users/me/revoke-sessions',
        method: 'POST',
      }),
    }),
    getImpersonationStatus: builder.query<
      {
        isImpersonating: boolean
        impersonatedUserEmail: string | null
        authenticatedAdminEmail: string | null
      },
      void
    >({
      query: () => 'v1/users/me/impersonation-status',
    }),
    startImpersonation: builder.mutation<
      { success: boolean; impersonatedEmail: string },
      { email: string }
    >({
      query: (data) => ({
        url: 'v1/users/me/impersonate',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
      async onQueryStarted({ email: _email }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // Optimistically update the impersonation status cache
          dispatch(
            userApi.util.updateQueryData('getImpersonationStatus', undefined, (draft) => {
              draft.isImpersonating = true
              draft.impersonatedUserEmail = data.impersonatedEmail
              // authenticatedAdminEmail will be set on next refetch, but we can leave it null for now
              draft.authenticatedAdminEmail = null
            })
          )
          // Emit localStorage event for cross-tab sync
          const statusChange = {
            timestamp: Date.now(),
            isImpersonating: true,
            impersonatedEmail: data.impersonatedEmail,
          }
          localStorage.setItem('impersonation-status-change', JSON.stringify(statusChange))
        } catch {
          // If mutation fails, cache will remain unchanged
        }
      },
    }),
    stopImpersonation: builder.mutation<{ success: boolean }, void>({
      query: () => ({
        url: 'v1/users/me/impersonate',
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          // Optimistically update the impersonation status cache
          dispatch(
            userApi.util.updateQueryData('getImpersonationStatus', undefined, (draft) => {
              draft.isImpersonating = false
              draft.impersonatedUserEmail = null
              draft.authenticatedAdminEmail = null
            })
          )
          // Emit localStorage event for cross-tab sync
          const statusChange = {
            timestamp: Date.now(),
            isImpersonating: false,
            impersonatedEmail: null,
          }
          localStorage.setItem('impersonation-status-change', JSON.stringify(statusChange))
        } catch {
          // If mutation fails, cache will remain unchanged
        }
      },
    }),
  }),
})

export const {
  useGetUserQuotaQuery,
  useGetUserAdminStatusQuery,
  useGetUserQuery,
  useSignOutMutation,
  useGetOnboardingDataQuery,
  useUpdateOnboardingDataMutation,
  useGetUserPromptQuery,
  useUpdateUserPromptMutation,
  useLazyGetUserQuery,
  useLazyGetUserQuotaQuery,
  useGetConnectedIntegrationsQuery,
  useRequestPasswordChangeMutation,
  useRevokeAllSessionsMutation,
  useGetImpersonationStatusQuery,
  useStartImpersonationMutation,
  useStopImpersonationMutation,
} = userApi

// Generic session-aware query hook factory
function createSessionAwareQuery<TQueryArg, TResult>(
  useQuery: (arg: TQueryArg, options: { skip: boolean }) => TResult
) {
  return function useSessionAwareQuery(arg: TQueryArg) {
    const [hasSession, setHasSession] = useState<boolean | null>(null)

    // Check for session on mount and when session-changed event fires
    useEffect(() => {
      const checkSession = async () => {
        const sessionExists = await Session.doesSessionExist()
        setHasSession(sessionExists)
      }
      checkSession()

      // Listen for session change events (e.g., after sign-in)
      const handleSessionChange = () => {
        checkSession()
      }
      window.addEventListener('session-changed', handleSessionChange)

      return () => {
        window.removeEventListener('session-changed', handleSessionChange)
      }
    }, [])

    // Only call the query if we have a session
    const queryResult = useQuery(arg, {
      skip: hasSession === false || hasSession === null,
    })

    // No-op refetch function
    const noOpRefetch = () =>
      Promise.resolve({
        data: undefined,
        error: undefined,
        isLoading: false,
        isSuccess: false,
        isError: false,
        isUninitialized: false,
      }) as any

    // If no session, return a controlled state
    if (hasSession === false) {
      return {
        ...queryResult,
        data: undefined,
        isLoading: false,
        isSuccess: false,
        error: undefined,
        refetch: noOpRefetch,
      }
    }

    // If session check is still loading, show loading state
    if (hasSession === null) {
      return {
        ...queryResult,
        data: undefined,
        isLoading: true,
        isSuccess: false,
        error: undefined,
        refetch: noOpRefetch,
      }
    }

    // Return normal query result when session exists
    return queryResult
  }
}

// Session-aware query hooks
export const useSessionAwareUserQuery = () => createSessionAwareQuery(useGetUserQuery)(undefined)
export const useSessionAwareUserQuotaQuery = () =>
  createSessionAwareQuery(useGetUserQuotaQuery)(undefined)
export const useSessionAwareOnboardingQuery = () =>
  createSessionAwareQuery(useGetOnboardingDataQuery)(undefined)
export const useSessionAwareConnectedIntegrationsQuery = (businessProfileId?: string) =>
  createSessionAwareQuery(useGetConnectedIntegrationsQuery)({ businessProfileId })
